/**
 * Admin accounts — the pure rules behind the super-admin-only "Admin accounts" section
 * on the Admin Team page (AdminAccountsSection.tsx).
 *
 * The Admin Portal is Google sign-in only, and a Google account can sign in only if an
 * Admin Portal account was already created for it here. The server decides every rule
 * below again (routers/admin_auth.py: CreateAdminRequest, admin_account_change_refusal);
 * these copies only keep the page from offering a change the server will refuse.
 */

export const ADMIN_GOOGLE_DOMAIN = 'coachcedar.com';

export const ADMIN_ROLES = ['super_admin', 'admin', 'support', 'viewer'] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

export const ROLE_LABELS: Record<AdminRole, string> = {
  super_admin: 'Super admin',
  admin: 'Admin',
  support: 'Support',
  viewer: 'Viewer',
};

export interface AdminAccount {
  admin_id: string;
  email: string;
  full_name: string | null;
  role: string;
  active: boolean;
  google_only: boolean;
  last_login_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AccountChange {
  role?: string;
  active?: boolean;
}

const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/** The address as it will be stored (trimmed, lower-case). */
export function normalizeAdminEmail(email: string): string {
  return (email || '').trim().toLowerCase();
}

/** Why a new account's email is not acceptable, or null. */
export function newAdminEmailError(email: string): string | null {
  const address = normalizeAdminEmail(email);
  if (!address) return 'Enter the Google email address.';
  if (!EMAIL_RE.test(address)) return 'That is not an email address.';
  if (address.split('@')[1] !== ADMIN_GOOGLE_DOMAIN) {
    return `Admin accounts must be ${ADMIN_GOOGLE_DOMAIN} Google accounts.`;
  }
  return null;
}

export function isAdminRole(role: string): role is AdminRole {
  return (ADMIN_ROLES as readonly string[]).includes(role);
}

export function activeSuperAdminCount(accounts: AdminAccount[]): number {
  return accounts.filter((a) => a.active && a.role === 'super_admin').length;
}

/**
 * Why this change must be refused, or null — the server's rule
 * (routers/admin_auth.admin_account_change_refusal): never deactivate or demote the LAST
 * active super admin, or no one could manage Admin Portal accounts again.
 */
export function accountChangeRefusal(
  account: AdminAccount,
  change: AccountChange,
  accounts: AdminAccount[],
): string | null {
  if (change.role !== undefined && !isAdminRole(change.role)) return 'Unknown role.';
  const isActiveSuper = account.active && account.role === 'super_admin';
  const stopsBeingActiveSuper =
    change.active === false || (change.role !== undefined && change.role !== 'super_admin');
  if (!isActiveSuper || !stopsBeingActiveSuper) return null;
  const others = accounts.filter(
    (a) => a.admin_id !== account.admin_id && a.active && a.role === 'super_admin',
  ).length;
  if (others === 0) {
    return 'This is the last active super admin. Make another account a super admin first.';
  }
  return null;
}

/** Active accounts first, then super admins, then by email. */
export function sortAccounts(accounts: AdminAccount[]): AdminAccount[] {
  const rank = (r: string) => {
    const i = (ADMIN_ROLES as readonly string[]).indexOf(r);
    return i === -1 ? ADMIN_ROLES.length : i;
  };
  return [...accounts].sort((a, b) => {
    if (a.active !== b.active) return a.active ? -1 : 1;
    if (rank(a.role) !== rank(b.role)) return rank(a.role) - rank(b.role);
    return a.email.localeCompare(b.email);
  });
}
